export interface TableBlockItem {
  html: string
}

export type TableBlockMarkdownSegment =
  | {
      type: 'markdown'
      markdown: string
    }
  | {
      type: 'tableBlock'
      table: TableBlockItem
    }

const TABLE_BLOCK_PATTERN = /<table\b[\s\S]*?<\/table>/gi
const TABLE_BLOCK_HTML_PATTERN =
  /<table-block\b([^>]*)>\s*<\/table-block>|<table-block\b([^>]*)\/>/g
const TABLE_BLOCK_PLACEHOLDER = 'KARAKURI_TABLE_BLOCK'

export const createDefaultTableBlockItem = (): TableBlockItem => ({
  html: `<table>
  <tr>
    <th></th>
    <th></th>
    <th></th>
  </tr>
  <tr>
    <td></td>
    <td></td>
    <td></td>
  </tr>
</table>`,
})

export function normalizeTableBlockItem(item: Partial<TableBlockItem>): TableBlockItem {
  return {
    html: normalizeTableHtml(item.html) || createDefaultTableBlockItem().html,
  }
}

export function encodeTableBlockItem(item: Partial<TableBlockItem>): string {
  return encodeURIComponent(JSON.stringify(normalizeTableBlockItem(item)))
}

export function decodeTableBlockItem(encodedItem: string | undefined): TableBlockItem {
  if (!encodedItem) {
    return createDefaultTableBlockItem()
  }

  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(encodedItem))
    if (!isTableBlockItemLike(parsed)) {
      return createDefaultTableBlockItem()
    }

    return normalizeTableBlockItem(parsed)
  } catch {
    return createDefaultTableBlockItem()
  }
}

export function serializeTableBlockMarkdown(item: Partial<TableBlockItem>): string {
  return normalizeTableBlockItem(item).html
}

export function splitTableBlocksFromMarkdown(markdown: string): TableBlockMarkdownSegment[] {
  const segments: TableBlockMarkdownSegment[] = []
  let lastIndex = 0

  markdown.replace(TABLE_BLOCK_PATTERN, (matchedText: string, offset: number) => {
    if (offset > lastIndex) {
      segments.push({ type: 'markdown', markdown: markdown.slice(lastIndex, offset) })
    }
    segments.push({ type: 'tableBlock', table: normalizeTableBlockItem({ html: matchedText }) })
    lastIndex = offset + matchedText.length
    return matchedText
  })

  if (lastIndex < markdown.length) {
    segments.push({ type: 'markdown', markdown: markdown.slice(lastIndex) })
  }

  return segments.length > 0 ? segments : [{ type: 'markdown', markdown }]
}

export function replaceTableBlockMarkdownWithPlaceholders(markdown: string): {
  markdown: string
  tables: TableBlockItem[]
} {
  const tables: TableBlockItem[] = []
  const replacedMarkdown = markdown.replace(TABLE_BLOCK_PATTERN, matchedText => {
    const index = tables.length
    tables.push(normalizeTableBlockItem({ html: matchedText }))
    return `${TABLE_BLOCK_PLACEHOLDER}_${index}`
  })

  return { markdown: replacedMarkdown, tables }
}

export function restoreTableBlockHtmlPlaceholders(html: string, tables: TableBlockItem[]): string {
  return tables.reduce((restoredHtml, table, index) => {
    const placeholder = `${TABLE_BLOCK_PLACEHOLDER}_${index}`
    const tableBlockHtml = serializeTableBlockMarkdown(table)
    return restoredHtml
      .replace(new RegExp(`<p>\\s*${placeholder}\\s*</p>`, 'g'), tableBlockHtml)
      .replace(new RegExp(placeholder, 'g'), tableBlockHtml)
  }, html)
}

export function replaceTableBlockHtmlWithPlaceholders(html: string): {
  html: string
  tables: TableBlockItem[]
} {
  const tables: TableBlockItem[] = []
  const replaceWithPlaceholder = (table: TableBlockItem) => {
    const index = tables.length
    tables.push(normalizeTableBlockItem(table))
    return `<p>${TABLE_BLOCK_PLACEHOLDER}_${index}</p>`
  }
  const htmlWithoutLegacyTableBlocks = html.replace(
    TABLE_BLOCK_HTML_PATTERN,
    (_matchedText, attributesA: string | undefined, attributesB: string | undefined) => {
      return replaceWithPlaceholder(
        decodeTableBlockItem(readHtmlAttribute(attributesA ?? attributesB ?? ''))
      )
    }
  )
  const replacedHtml = htmlWithoutLegacyTableBlocks.replace(TABLE_BLOCK_PATTERN, matchedText =>
    replaceWithPlaceholder({ html: matchedText })
  )

  return { html: replacedHtml, tables }
}

export function restoreTableBlockMarkdownPlaceholders(
  markdown: string,
  tables: TableBlockItem[]
): string {
  return tables.reduce(
    (restoredMarkdown, table, index) =>
      restoredMarkdown.replace(
        createMarkdownPlaceholderRegExp(`${TABLE_BLOCK_PLACEHOLDER}_${index}`),
        serializeTableBlockMarkdown(table)
      ),
    markdown
  )
}

function readHtmlAttribute(attributes: string): string | undefined {
  const matched = /data-table=(?:"([^"]*)"|'([^']*)'|([^\s>]+))/.exec(attributes)
  return matched?.[1] ?? matched?.[2] ?? matched?.[3]
}

function normalizeTableHtml(html: string | undefined): string {
  return (
    html
      ?.trim()
      .replace(/<colgroup[\s\S]*?<\/colgroup>/gi, '')
      .replace(/\sstyle=(?:"min-width:\s*75px;?"|'min-width:\s*75px;?')/gi, '')
      .replace(
        /<br\s+class=(?:"ProseMirror-trailingBreak"|'ProseMirror-trailingBreak')\s*\/?>/gi,
        ''
      )
      .replace(/>\s+</g, '><') ?? ''
  )
}

function createMarkdownPlaceholderRegExp(placeholder: string): RegExp {
  return new RegExp(escapeRegExp(placeholder).replace(/_/g, '\\\\?_'), 'g')
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function isTableBlockItemLike(value: unknown): value is Partial<TableBlockItem> {
  return typeof value === 'object' && value !== null
}
